/**
 * Get hls.js loading again when a jump finds it stopped.
 *
 * On the iPhone and iPad hls.js plays through ManagedMediaSource,
 * which tells it when to stop fetching ("endstreaming": enough is
 * buffered) and when to start again ("startstreaming"). Between the
 * two, hls.js loads nothing at all. A jump to somewhere not buffered
 * ought to bring a "startstreaming"; on the iPhone it did not, and the
 * video sat at the new place with nothing asked for - the playback log
 * said "seeking to 4149.0s" and then not one request followed.
 *
 * So after a seek, or when the video is waiting, if no segment starts
 * loading within NOTHING_LOADING_MS and the position is not buffered,
 * hls.js is told to carry on: its own resumeBuffering, the same call
 * its "startstreaming" handler makes. Anywhere else a seek starts
 * loading at once, so this never fires there. Nor while Safari's own
 * player has the video, on AirPlay or handed over - hls.js loading
 * then would be loading for nothing.
 */

// Short: on the iPhone a jump never brought loading back by itself in
// five tries out of five, so every jump waited this long for nothing.
export const NOTHING_LOADING_MS = 300;

// Whether there is picture to play at the current position.
const isBuffered = (media: HTMLMediaElement) => {
  const at = media.currentTime;
  const ranges = media.buffered;
  for (let i = 0; i < (ranges?.length ?? 0); i += 1) {
    if (ranges.start(i) <= at + 0.1 && at < ranges.end(i) - 0.5) {
      return true;
    }
  }
  return false;
};

/** Watches one hls.js instance. Returns what stops the watching. */
export const keepLoadingAfterSeek = (
  hls: any,
  note?: (text: string) => void,
) => {
  let lastLoad = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let media: any = null;

  const onLoading = () => {
    lastLoad = Date.now();
  };

  const check = (since: number) => {
    // Only where hls.js plays through ManagedMediaSource, which is what
    // stops it. Elsewhere a jump loads at once, and telling hls.js to
    // carry on this soon would only restart what it had begun.
    if (!('ManagedMediaSource' in self)) {
      return;
    }
    const watched = hls.media;
    if (!watched || lastLoad >= since || isBuffered(watched)) {
      return;
    }
    if (
      watched.webkitCurrentPlaybackTargetIsWireless ||
      !String(watched.currentSrc || '').startsWith('blob:')
    ) {
      return;
    }
    note?.(
      `nothing loading ${(NOTHING_LOADING_MS / 1000).toFixed(1)}s after a jump to ${watched.currentTime.toFixed(1)}s: hls.js told to carry on`,
    );
    hls.resumeBuffering?.();
  };

  const arm = () => {
    clearTimeout(timer);
    const since = Date.now();
    timer = setTimeout(() => check(since), NOTHING_LOADING_MS);
  };

  const forget = () => {
    media?.removeEventListener?.('seeking', arm);
    media?.removeEventListener?.('waiting', arm);
    media = null;
  };

  const onAttached = () => {
    forget();
    media = hls.media;
    media?.addEventListener?.('seeking', arm);
    media?.addEventListener?.('waiting', arm);
  };

  hls.on('hlsFragLoading', onLoading);
  hls.on('hlsMediaAttached', onAttached);
  if (hls.media) {
    onAttached();
  }

  return () => {
    clearTimeout(timer);
    forget();
    hls.off?.('hlsFragLoading', onLoading);
    hls.off?.('hlsMediaAttached', onAttached);
  };
};
